import React from "react";
import { Navigate, Route, Routes } from "react-router";

import { FullPageLoader } from "./components";
import { UnauthorizedPage } from "./pages/common";
import { useAuth } from "./contexts";
import { ActionCode } from "./api";
import AgentItemPage from "./pages/agents/item/AgentItemPage";
import MentionItemPage from "./pages/mentions/MentionItemPage";
import FormQuestionItemPage from "./pages/formQuestions/item/FormQuestionItemPage";
import FormQuestionOperationalItemPage from "./pages/formQuestionOperationals/item/FormQuestionOperationalItemPage";
import FunctionItemPage from "./pages/functions/FunctionItemPage";
import FunctionalDirectorListPage from "./pages/functionalDirectors/list/FunctionalDirectorListPage";
import FunctionalDirectorItemPage from "./pages/functionalDirectors/item/FunctionalDirectorItemPage";
import ObjectiveQuestionItemPage from "./pages/objectiveQuestions/item/ObjectiveQuestionItemPage";
import CancellationReasonItemPage from "./pages/cancellationReasons/item/CancellationReasonItemPage";

export enum ERoutes {
  translations = "/translations",
  dataTranslations = "/dataTranslations",
  notFound = "/notFound",
  unauthorized = "/unauthorized",
  users = "/users",
  home = "/",
  actionRoles = "/actionRoles",
  agents = "/agents",
  gradeFunctions = "/gradeFunctions",
  dossiers = "/dossiers",
  mentions = "/mentions",
  formQuestions = "/formQuestions",
  formQuestionOperationals = "/formQuestionOperationals",
  dashboard = "dashboard",
  functions = "/functions",
  functionalDirectors = "/functionalDirectors",
  objectiveQuestions = "/objectiveQuestions",
  newOperationalInterview = "/newOperationalInterview",
  cancellationReasons = "/cancellationReasons",
}

const getEditRoute = (base: ERoutes) => "" + base + "/:id/:tab";

const UserItemPage = React.lazy(
  () => import("./pages/users/item/userItemPage")
);
const UserListPage = React.lazy(
  () => import("./pages/users/list/userListPage")
);
const TranslationEditPage = React.lazy(
  () => import("./pages/translation/TranslationEditPage")
);
const DataTranslationEditPage = React.lazy(
  () => import("./pages/translation/DataTranslationEditPage")
);
const ActionRoleListPage = React.lazy(
  () => import("./pages/actionRoles/list/ActionRoleListPage")
);

const AgentListPage = React.lazy(
  () => import("./pages/agents/list/AgentListPage")
);

const GradeFunctionListPage = React.lazy(
  () => import("./pages/gradeFunctions/list/GradeFunctionListPage")
);

const AssessementDossierListPage = React.lazy(
  () => import("./pages/assessementDossiers/list/AssessementDossierListPage")
);

const AssessementDossierItemPage = React.lazy(
  () => import("./pages/assessementDossiers/item/AssessementItemPage")
);

const MentionListPage = React.lazy(
  () => import("./pages/mentions/MentionListPage")
);

const FormQuestionListPage = React.lazy(
  () => import("./pages/formQuestions/list/FormQuestionListPage")
);

const FormQuestionOperationalListPage = React.lazy(
  () =>
    import("./pages/formQuestionOperationals/list/FormQuestionOperationalPage")
);

const DashboardListPage = React.lazy(
  () => import("./pages/dashboards/DashboardListPage")
);

const FunctionListPage = React.lazy(
  () => import("./pages/functions/FunctionListPage")
);

const ObjectiveQuestionListPage = React.lazy(
  () => import("./pages/objectiveQuestions/list/ObjectiveQuestionListPage")
);

const CancellationReasonListPage = React.lazy(
  () => import("./pages/cancellationReasons/list/CancellationReasonListPage")
);

export const AppRouter = () => {
  const { hasAction } = useAuth();
  return (
    <React.Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route
          path={ERoutes.functionalDirectors}
          element={<FunctionalDirectorListPage />}
          action={hasAction([ActionCode.ViewFunctionalDirector])}
        />
        <Route
          path={getEditRoute(ERoutes.functionalDirectors)}
          element={<FunctionalDirectorItemPage />}
          action={hasAction([ActionCode.ViewFunctionalDirector])}
        />
        <Route
          path={ERoutes.functions}
          element={<FunctionListPage />}
          action={hasAction([ActionCode.ViewFunction])}
        />
        <Route
          path={getEditRoute(ERoutes.functions)}
          element={<FunctionItemPage />}
          action={hasAction([ActionCode.ViewFunction])}
        />
        <Route
          path={ERoutes.dashboard}
          element={<DashboardListPage />}
          action={hasAction([ActionCode.ViewDashboard])}
        />
        <Route
          path={ERoutes.dossiers}
          element={<AssessementDossierListPage />}
          action={hasAction([ActionCode.ViewDossier])}
        />
        <Route
          path={ERoutes.dossiers + "/:id/:tab/:subtab?/:subid?"}
          element={<AssessementDossierItemPage />}
          action={hasAction([ActionCode.ViewAgent])}
        />
        <Route
          path={ERoutes.agents}
          element={<AgentListPage />}
          action={hasAction([ActionCode.ViewAgent])}
        />
        <Route
          path={getEditRoute(ERoutes.agents)}
          element={<AgentItemPage />}
          action={hasAction([ActionCode.ViewAgent])}
        />
        <Route
          path={ERoutes.gradeFunctions}
          element={<GradeFunctionListPage />}
          action={hasAction([ActionCode.ViewGradeFunctions])}
        />
        <Route
          path={getEditRoute(ERoutes.mentions)}
          element={<MentionItemPage />}
          action={hasAction([ActionCode.ViewMention])}
        />
        <Route
          path={ERoutes.mentions}
          element={<MentionListPage />}
          action={hasAction([ActionCode.ViewMention])}
        />
        <Route
          path={getEditRoute(ERoutes.formQuestions)}
          element={<FormQuestionItemPage />}
          action={hasAction([ActionCode.ViewFormQuestion])}
        />
        <Route
          path={ERoutes.formQuestions}
          element={<FormQuestionListPage />}
          action={hasAction([ActionCode.ViewFormQuestion])}
        />
        <Route
          path={getEditRoute(ERoutes.objectiveQuestions)}
          element={<ObjectiveQuestionItemPage />}
          action={hasAction([ActionCode.ViewObjectiveQuestion])}
        />
        <Route
          path={ERoutes.objectiveQuestions}
          element={<ObjectiveQuestionListPage />}
          action={hasAction([ActionCode.ViewObjectiveQuestion])}
        />
        <Route
          path={getEditRoute(ERoutes.cancellationReasons)}
          element={<CancellationReasonItemPage />}
          action={hasAction([ActionCode.ViewCancellationReason])}
        />
        <Route
          path={ERoutes.cancellationReasons}
          element={<CancellationReasonListPage />}
          action={hasAction([ActionCode.ViewCancellationReason])}
        />
        <Route
          path={getEditRoute(ERoutes.formQuestionOperationals)}
          element={<FormQuestionOperationalItemPage />}
          action={hasAction([ActionCode.ViewFormQuestion])}
        />
        <Route
          path={ERoutes.formQuestionOperationals}
          element={<FormQuestionOperationalListPage />}
          action={hasAction([ActionCode.ViewFormQuestion])}
        />
        <Route
          path={`${ERoutes.translations}`}
          element={<TranslationEditPage />}
          action={hasAction([ActionCode.EditTranslations])}
        />
        <Route
          path={`${ERoutes.dataTranslations}`}
          element={<DataTranslationEditPage />}
          action={hasAction([ActionCode.EditDataTranslations])}
        />
        <Route
          path={ERoutes.users}
          element={<UserListPage />}
          action={hasAction([ActionCode.ViewUser])}
        />
        <Route
          path={getEditRoute(ERoutes.users)}
          element={<UserItemPage />}
          action={hasAction([ActionCode.EditUser])}
        />
        <Route
          path={ERoutes.actionRoles}
          element={<ActionRoleListPage />}
          action={hasAction([ActionCode.ViewUserActions])}
        />
        <Route
          path={`${ERoutes.unauthorized}`}
          element={<UnauthorizedPage />}
        />
        <Route path="*" element={<Navigate to={ERoutes.dashboard} />} />
      </Routes>
    </React.Suspense>
  );
};
